<script>
import FlowPagination from '@/components/FlowPagination.vue'
import Loading from '@/components/Loading.vue'
import ShowStatus from '@/components/ShowStatus.vue'
import http from '@/utils/http'
import { formatDate, toUtcIsoString } from '@/utils/transform'

export default {
  name: 'DomainEventIndex',
  components: {
    FlowPagination,
    Loading,
    ShowStatus,
  },
  data() {
    return this.getInitialState()
  },
  computed: {
    eventTypeConfig() {
      return {
        Created: { label: 'Criação', color: '#4CAF50' },
        Updated: { label: 'Edição', color: '#2196F3' },
        Deleted: { label: 'Exclusão', color: '#F44336' },
      }
    },
  },
  watch: {
    $route() {
      this.updateStateFromQuery()
      this.searchEvents()
    },
  },
  async beforeMount() {
    this.updateStateFromQuery()
    await Promise.all([this.loadEntityTypes(), this.searchEvents()])
  },
  methods: {
    getInitialState() {
      return {
        events: null,
        entityTypes: [],
        filter: {
          entityType: null,
          entityId: null,
          title: null,
          dataInicio: null,
          dataFim: null,
        },
        filterTemp: {
          entityType: null,
          entityId: null,
          title: null,
          dataInicio: null,
          dataFim: null,
        },
        currentPage: 1,
        pageSize: 20,
        dialog: {
          open: false,
          event: null,
        },
      }
    },
    updateStateFromQuery() {
      const initialState = this.getInitialState()
      const query = this.$route.query

      this.filter.entityType = query.entityType ?? null
      this.filter.entityId = query.entityId ?? null
      this.filter.title = query.title ?? null
      this.filter.dataInicio = query.dataInicio
        ? new Date(query.dataInicio)
        : null
      this.filter.dataFim = query.dataFim ? new Date(query.dataFim) : null
      this.filterTemp = { ...this.filter }
      this.currentPage = parseInt(query.page) || initialState.currentPage
      this.pageSize = parseInt(query.pageSize) || initialState.pageSize
    },
    handleApplyFilters() {
      this.filter = { ...this.filterTemp }
      this.updateRoute(1)
    },
    handleLimparFiltro() {
      const initialState = this.getInitialState()
      this.filterTemp = { ...initialState.filterTemp }
    },
    handlePageChange(page) {
      this.updateRoute(page)
    },
    updateRoute(page) {
      const queryParams = {
        entityType: this.filter.entityType,
        entityId: this.filter.entityId,
        title: this.filter.title,
        dataInicio: toUtcIsoString(this.filter.dataInicio),
        dataFim: toUtcIsoString(this.filter.dataFim),
        page: page,
        pageSize: this.pageSize,
      }

      const filteredQuery = Object.fromEntries(
        Object.entries(queryParams).filter(
          ([, value]) => value !== null && value !== ''
        )
      )

      this.$router.push({ query: filteredQuery }).catch(() => {})
    },
    async loadEntityTypes() {
      const response = await http.get('admin/domain-events/types')
      this.entityTypes = response?.entityTypes ?? []
    },
    async searchEvents() {
      this.events = null

      const response = await http.get('admin/domain-events', {
        ...this.getFilter(),
        page: this.currentPage,
        pageSize: this.pageSize,
      })

      this.events = response?.events ?? []
    },
    getFilter() {
      const filter = {}

      if (this.filter.entityType) {
        filter.entityType = this.filter.entityType
      }

      if (this.filter.entityId) {
        filter.entityId = this.filter.entityId
      }

      if (this.filter.title) {
        filter.title = this.filter.title
      }

      if (this.filter.dataInicio) {
        filter.dataInicio = toUtcIsoString(this.filter.dataInicio)
      }

      if (this.filter.dataFim) {
        filter.dataFim = toUtcIsoString(this.filter.dataFim)
      }

      return filter
    },
    handleVerDados(event) {
      this.dialog = {
        open: true,
        event,
      }
    },
    entityTypeShortName(entityType) {
      if (!entityType) {
        return '-'
      }

      return entityType.split('.').pop()
    },
    entityName(event) {
      if (event.entityType !== 'Flow.Core.Entities.ApplicationUser') {
        return null
      }

      const name = event.data?.entityName

      return name ? name.replace('Flow.Core.Entities.', '') : null
    },
    entityLink(event) {
      if (event.entityType !== 'Flow.Core.Entities.ApplicationUser') {
        return null
      }

      // Entidade excluída não tem mais página para abrir
      if (this.eventTypeKey(event) === 'Deleted') {
        return null
      }

      const data = event.data ?? {}
      const entityId = data.entityId
      const entity = data.entity ?? {}
      const localId = entity.localId

      if (!data.entityName || !entityId) {
        return null
      }

      const links = {
        'Flow.Core.Entities.Local': () => ({
          name: 'LocalEditar',
          params: { localId: entityId },
        }),
        'Flow.Core.Entities.ApplicationUser': () => ({
          name: 'usuariosEditar',
          params: { userId: entityId },
        }),
        'Flow.Core.Entities.TemplateEquipamento': () => ({
          name: 'EquipamentoTemplateEditar',
          params: { templateId: entityId },
        }),
        'Flow.Core.Entities.EquipamentoCampo': () =>
          localId && {
            name: 'EquipamentoCampoEditar',
            params: { localId, equipamentoId: entityId },
          },
        'Flow.Core.Entities.Notificacao': () =>
          localId && {
            name: 'NotificacaoEditar',
            params: { localId, notificacaoId: entityId },
          },
        'Flow.Core.Entities.NotificacaoAcao': () =>
          entity.notificacao.localId && {
            name: 'NotificacaoEditar',
            params: {
              localId: entity.notificacao.localId,
              notificacaoId: entity.notificacao.id,
            },
          },
        'Flow.Core.Entities.SensorVirtual': () =>
          localId && {
            name: 'SensorVirtualEdit',
            params: { localId, sensorVirtualId: entityId },
          },
        'Flow.Core.Entities.Cargo': () =>
          localId && {
            name: 'CargoEditar',
            params: { localId, cargoId: entityId },
          },
      }

      const buildLink = links[data.entityName]

      return buildLink ? buildLink() || null : null
    },
    eventTypeKey(event) {
      const eventType = event.data?.eventType

      if (eventType && this.eventTypeConfig[eventType]) {
        return eventType
      }

      // Eventos de edição do tracker não têm eventType, mas têm o objeto changes
      if (event.data?.changes) {
        return 'Updated'
      }

      return null
    },
    formatJson(data) {
      return JSON.stringify(data, null, 2)
    },
    formatDate,
  },
}
</script>

<template>
  <div>
    <div class="md-layout md-gutter" style="justify-content: flex-end">
      <div class="md-layout-item md-size-10">
        <p class="md-title">Domain Events</p>
      </div>

      <div class="md-layout-item md-size-20">
        <md-field>
          <label for="entityType">Entity Type</label>
          <md-select
            id="entityType"
            v-model="filterTemp.entityType"
            name="entityType"
          >
            <md-option :value="null">
              Todos (exceto AcoesEquipamento)
            </md-option>
            <md-option
              v-for="entityType in entityTypes"
              :key="entityType"
              :value="entityType"
            >
              {{ entityTypeShortName(entityType) }}
            </md-option>
          </md-select>
        </md-field>
      </div>

      <div class="md-layout-item md-size-10">
        <md-field>
          <label for="entityId">Entity Id</label>
          <md-input
            id="entityId"
            v-model="filterTemp.entityId"
            name="entityId"
            type="number"
          />
        </md-field>
      </div>

      <div class="md-layout-item md-size-10">
        <md-field>
          <label for="title">Título</label>
          <md-input id="title" v-model="filterTemp.title" name="title" />
        </md-field>
      </div>

      <div class="md-layout-item md-size-15">
        <span class="md-caption">Data inicial</span>
        <VueCtkDateTimePicker
          id="dataInicial"
          v-model="filterTemp.dataInicio"
          :no-label="true"
          label="Selecionar data"
          format="YYYY-MM-DD HH:mm"
        />
      </div>

      <div class="md-layout-item md-size-15">
        <span class="md-caption">Data final</span>
        <VueCtkDateTimePicker
          id="dataFim"
          v-model="filterTemp.dataFim"
          :no-label="true"
          label="Selecionar data"
          format="YYYY-MM-DD HH:mm"
        />
      </div>

      <div class="md-layout-item md-size-10">
        <md-button
          class="md-raised md-primary mt-4"
          @click="() => handleLimparFiltro()"
        >
          <md-icon>clear</md-icon>
          Limpar
        </md-button>
      </div>

      <div class="md-layout-item md-size-10">
        <md-button
          class="md-raised md-primary mt-4"
          @click="() => handleApplyFilters()"
        >
          <md-icon>search</md-icon>
          Buscar
        </md-button>
      </div>
    </div>
    <hr />
    <Loading :loading="!events" />
    <div v-if="events">
      <div v-if="events.length" style="min-height: 60vh">
        <md-table>
          <md-table-row>
            <md-table-head>Data</md-table-head>
            <md-table-head>Entity Type</md-table-head>
            <md-table-head>Entity Id</md-table-head>
            <md-table-head>Título</md-table-head>
            <md-table-head>Evento</md-table-head>
            <md-table-head>Usuário</md-table-head>
            <md-table-head />
          </md-table-row>
          <md-table-row v-for="event in events" :key="event.id">
            <md-table-cell>{{ formatDate(event.criadoEm) }}</md-table-cell>
            <md-table-cell>
              {{ entityTypeShortName(event.entityType) }}
              <md-tooltip md-direction="top">
                {{ event.entityType }}
              </md-tooltip>
            </md-table-cell>
            <md-table-cell>
              {{ event.entityId }}
              <span v-if="entityName(event)"> ({{ entityName(event) }})</span>
            </md-table-cell>
            <md-table-cell>{{ event.title }}</md-table-cell>
            <md-table-cell>
              <show-status
                v-if="eventTypeKey(event)"
                :value="eventTypeKey(event)"
                :config="eventTypeConfig"
              />
              <span v-else>-</span>
            </md-table-cell>
            <md-table-cell>{{ event.userName || '-' }}</md-table-cell>
            <md-table-cell>
              <md-button
                class="md-icon-button"
                @click="() => handleVerDados(event)"
              >
                <md-icon>data_object</md-icon>
                <md-tooltip md-direction="left"> Ver dados </md-tooltip>
              </md-button>
              <router-link v-if="entityLink(event)" :to="entityLink(event)">
                <md-button class="md-icon-button">
                  <md-icon>open_in_new</md-icon>
                  <md-tooltip md-direction="left"> Abrir registro </md-tooltip>
                </md-button>
              </router-link>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </div>
      <div v-else class="align-center mt-5">
        <md-empty-state
          md-rounded
          md-icon="info"
          md-label="Nenhum evento encontrado"
          md-description="Nenhum domain event foi encontrado com os filtros aplicados"
        />
      </div>
      <flow-pagination
        class="mt-3"
        :current-page="currentPage"
        :page-count="events.length"
        :page-size="pageSize"
        :pages-to-show="0"
        :should-show-pages="false"
        @change="handlePageChange"
      />
    </div>

    <md-dialog :md-active.sync="dialog.open">
      <md-dialog-title>
        Dados do evento
        <span v-if="dialog.event" class="md-caption">
          #{{ dialog.event.id }}
        </span>
      </md-dialog-title>
      <md-dialog-content v-if="dialog.event">
        <p>
          <strong>{{ dialog.event.entityType }}</strong>
          - Entity Id: {{ dialog.event.entityId }}
        </p>
        <p>
          {{ dialog.event.title }} - {{ formatDate(dialog.event.criadoEm) }}
        </p>
        <pre class="json-content">{{ formatJson(dialog.event.data) }}</pre>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="dialog.open = false">
          Fechar
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<style scoped lang="scss">
.json-content {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 12px;
  max-height: 50vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
